import { useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { StateContext } from "../context/globalContext/context";
import { iGlobalContext } from "../context/globalContext/interface";
import { ROUTES } from "../constant";
import { ScreenTypes } from "../enums";
import { INITIAL_BREADCRUMBS_LIST } from "../constant/bookingInProgressConstant/breadCrumbsConstant";
import { Messages } from "../constant/messages/toastMessages";
import { iSharedCartFlightSearch, iSharedCartSearch, iSharedCartStay } from "../api/sharedCart/sharedCart";
import { AirPort, iHotelInputPayload, RoomInfo } from "../types";
import { flightOrHotelSelect } from "../utils/booking/breadCrumb/breadCrumbUtils";
import { clearChatBookingOrigin } from "../utils/booking/clearChatBookingOrigin";
import { toMapAreaStay } from "../utils/booking/hotel/mapAreaStay";
import { toastMessage } from "../helpers/toast/toastMessage";
import { clearSharedCartView, getSharedCartSearch } from "./useIsSharedCartView";

const toShopStay = (stay: iSharedCartStay): iHotelInputPayload => {
  const place = stay.city || stay.address || "";
  const entry: iHotelInputPayload = {
    checkIn: stay.checkIn,
    checkOut: stay.checkOut,
    country: stay.country,
    city: place,
    latitude: stay.latitude,
    longitude: stay.longitude,
    locality: place,
    accommodation_location_name: place,
    isSelected: true,
    selectedLocation: place,
    ...(stay.hotelName ? { hotelName: stay.hotelName } : {}),
  };
  return stay.radiusMiles && stay.latitude && stay.longitude
    ? toMapAreaStay(entry, { latitude: stay.latitude, longitude: stay.longitude }, stay.radiusMiles, {
        city: place,
        country: stay.country,
      })
    : entry;
};

const toShopLeg = (departure: string, arrival: string, departureDate: string, returnDate?: string): AirPort => ({
  departure,
  arrival,
  departureAddress: departure,
  arrivalAddress: arrival,
  departureCityAddress: departure,
  arrivalCityAddress: arrival,
  departureAirportFullName: departure,
  arrivalAirportFullName: arrival,
  fromCity: departure,
  destinationCity: arrival,
  departureDate,
  returnDate: returnDate ?? departureDate,
  noOfDays: 1,
});

const toShopAirPorts = (flight: iSharedCartFlightSearch): AirPort[] => {
  if (flight.tripType === 3) return flight.segments.map((s) => toShopLeg(s.departure, s.arrival, s.departureDate));
  const [first] = flight.segments;
  const back = first.returnDate || first.departureDate;
  return [toShopLeg(first.departure, first.arrival, first.departureDate, back), toShopLeg(first.arrival, first.departure, back, back)];
};

const roomFields = (rooms: RoomInfo[]) =>
  rooms.length
    ? {
        roomInfo: rooms,
        numberOfRoomCount: rooms.length,
        adultsHotelCount: rooms.reduce((sum, room) => sum + (room.adults || 0), 0),
        childrenHotelCount: rooms.reduce((sum, room) => sum + (room.child || 0), 0),
      }
    : {};

export function useSharedCartFallback() {
  const {
    isMobile,
    setBookingDetails,
    setSelectedFlightOptions,
    setSelectedHotelRoomArr,
    setPromoReviewCartData,
    setFlightData,
    setHotelData,
    setFlightAvailabilityData,
    setHasTriggeredPreBooking,
    setShouldAutoCallSearchFlightHotel,
    setHideDetails,
    setBreadList,
    setActiveBreadCrumbs,
    setScreen,
    setFirstScreen,
  }: iGlobalContext = useContext(StateContext);
  const navigate = useNavigate();

  return useCallback(
    (failedLoad: boolean) => {
      const goHome = () => {
        setSelectedFlightOptions({ flights: false, hotels: false, activities: false });
        setSelectedHotelRoomArr([]);
        setBreadList(INITIAL_BREADCRUMBS_LIST as any);
        setActiveBreadCrumbs(INITIAL_BREADCRUMBS_LIST[0]);
        setScreen("");
        setFirstScreen("");
        navigate(ROUTES.Home, { replace: true });
      };

      const searchAgain = (search: iSharedCartSearch) => {
        const options = { flights: !!search.flight, hotels: !!search.hotel, activities: false };
        setBookingDetails((prev: any) => ({
          ...prev,
          selectedFlightId: "",
          isFlightAvailable: false,
          isFlightAvailableForPrebooking: false,
          ...(search.hotel ? { hotels: search.hotel.stays.map(toShopStay), ...roomFields(search.hotel.rooms) } : {}),
          ...(search.flight
            ? {
                airPort: toShopAirPorts(search.flight),
                tripType: search.flight.tripType,
                isThisMultiCityTrip: search.flight.tripType === 3,
                flightClass: search.flight.cabin,
                adultsCount: search.flight.adults,
                seniorsCount: 0,
                childrenCount: search.flight.children,
                infantCount: search.flight.infants,
                infantLapCount: 0,
              }
            : {}),
        }));
        setSelectedFlightOptions(options);
        flightOrHotelSelect(options, setBreadList, setActiveBreadCrumbs);
        setShouldAutoCallSearchFlightHotel(true);
        setHideDetails(false);
        setScreen(ScreenTypes.newBooking);
        navigate(isMobile ? ROUTES.BookingInProgress : ROUTES.Home, { replace: true });
      };

      const search = getSharedCartSearch();
      clearSharedCartView();
      clearChatBookingOrigin();
      setSelectedHotelRoomArr([]);
      setPromoReviewCartData(null);
      setFlightData(null);
      setHotelData(null);
      setFlightAvailabilityData(null);
      setHasTriggeredPreBooking(false);
      if (failedLoad) {
        const copy = Messages[search ? "shared.cart.load.failed" : "shared.cart.open.failed"];
        toastMessage.error(copy.messageTitle, copy.message);
      }
      if (search) searchAgain(search);
      else goHome();
    },
    [
      isMobile,
      navigate,
      setBookingDetails,
      setSelectedFlightOptions,
      setSelectedHotelRoomArr,
      setPromoReviewCartData,
      setFlightData,
      setHotelData,
      setFlightAvailabilityData,
      setHasTriggeredPreBooking,
      setShouldAutoCallSearchFlightHotel,
      setHideDetails,
      setBreadList,
      setActiveBreadCrumbs,
      setScreen,
      setFirstScreen,
    ]
  );
}
